/**
 * Micheals Infrastructure Advisory - INTERACTIVE BOOKING GATEWAY
 * VERSION: 4.2.1 (Stability Patch - Gateway Handshake)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { 
  Loader2, ShieldCheck, Clock, CheckCircle2, 
  Zap, Microscope, Landmark, AlertCircle
} from 'lucide-react'; 
import { useSalesEngine } from '../hooks/useSalesEngine';
import { secureFetch } from "@utils/apiClient";

const InteractiveBooking = ({ onBookingComplete, sessionId, leadName, leadEmail, initialService }) => {
  const [step, setStep] = useState(0); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [error, setError] = useState(null);
  const { syncLeadData } = useSalesEngine();

  const activeSessionId = sessionId || "TELEMETRY_ALPHA_2026";

  const tiers = [
    { id: 'DISCOVERY', name: 'Option A', label: 'Discovery Tech Call', price: 7500, displayPrice: '$75', duration: '20-Min Focused Sync', icon: <Zap className="w-5 h-5 text-amber-500" />, desc: 'Rapid diagnostic for specific technical friction points.' },
    { id: 'FORENSIC', name: 'Option B', label: 'Forensic Review', price: 15000, displayPrice: '$150', duration: '45-Min Deep Dive', icon: <Microscope className="w-5 h-5 text-amber-500" />, desc: 'Infrastructure audit & documentation analysis.' },
    { id: 'STRATEGIC', name: 'Option C', label: 'Phase 02: Strategic Briefing', price: 25000, displayPrice: '$250', duration: '60-Min Intensive', icon: <Landmark className="w-5 h-5 text-amber-500" />, desc: 'Full specification oversight and project roadmap.' }
  ];

  useEffect(() => {
    syncLeadData({ event: "BOOKING_GATEWAY_OPENED", sessionId: activeSessionId });
  }, [syncLeadData, activeSessionId]);

  const handleFinalize = async () => {
    if (!selectedTier) return;
    setIsSubmitting(true);
    setError(null);
    
    try {
      const numericAmount = Math.round(selectedTier.price / 100);

      // Sync state to Telemetry engine
      await syncLeadData({ 
        event: "TIER_SELECTED", 
        tier: selectedTier.name,
        amount: numericAmount,
        sessionId: activeSessionId 
      });

      // Secure handshake with the Provisioning Gateway
      const response = await secureFetch('/api/provisioning/initiate-booking', {
        method: 'POST',
        body: JSON.stringify({ 
          email: leadEmail || "info@medconsulting.solutions", 
          leadName: leadName || "Valued Client", 
          sessionId: activeSessionId, 
          message: `Client Initiated Tier Selection: ${selectedTier.name}`,
          tier: selectedTier.name, 
          amount: numericAmount,   
          concentration: initialService || "General_Infrastructure_Architecture"
        }),
      });
      
      if (response && response.url) {
          window.location.assign(response.url);
      } else {
          throw new Error("Invalid Handshake: Dispatch URL missing.");
      }
    } catch (err) {
      console.error("❌ [MED_GATEWAY_ERROR]:", err);
      setError("Technical handshake failed. Please ensure your session is active.");
      setIsSubmitting(false);
    }
  };

  // UI rendering remains consistent with your design patterns
  return (
    <div className="w-full max-w-lg mx-auto bg-white border border-stone-100 rounded-[2.5rem] shadow-2xl overflow-hidden">
      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-stone-50 rounded-full border border-stone-100">
                <ShieldCheck className="w-8 h-8 text-stone-900" />
              </div>
            </div>
            <h2 className="text-2xl font-serif italic text-stone-900 mb-4">Strategic Authorization</h2>
            <p className="text-stone-500 mb-8 text-sm leading-relaxed">
              Select the engagement tier required for your infrastructure diagnostic. All sessions are logged for forensic oversight.
            </p>
            <button onClick={() => setStep(1)} className="w-full py-4 bg-stone-950 text-white rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:bg-amber-800 transition-all">
              Review Engagement Tiers
            </button>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div key="tiers" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-10">
            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-700 mb-6">Select Allocation</h3>
            <div className="space-y-4 mb-8">
              {tiers.map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier)}
                  className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${
                    selectedTier?.id === tier.id ? 'border-amber-600 bg-amber-50/30' : 'border-stone-50 bg-stone-50/50 hover:border-stone-200'
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                      {tier.icon}
                      <span className="font-bold text-stone-900 text-sm">{tier.label}</span>
                    </div>
                    <span className="text-sm font-black text-stone-900">{tier.displayPrice}</span>
                  </div>
                  <p className="text-[11px] text-stone-500 leading-relaxed">{tier.desc}</p>
                </button>
              ))}
            </div>
            <button 
              disabled={!selectedTier}
              onClick={() => setStep(2)}
              className="w-full py-4 bg-stone-950 text-white rounded-2xl font-bold uppercase tracking-widest text-[10px] disabled:opacity-30"
            >
              Continue to Authorization
            </button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="confirm" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-12">
            {/* Confirmation logic displays summary and finalizes payment */}
            <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100 mb-6">
                <h4 className="text-[10px] font-black uppercase text-stone-400 mb-4 tracking-tighter">Engagement Summary</h4>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-bold">{selectedTier?.label}</span>
                  <span className="text-sm font-black">{selectedTier?.displayPrice}</span>
                </div>
                <div className="flex items-center gap-2 text-stone-500 text-[11px]">
                  <Clock size={12} /> {selectedTier?.duration}
                </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-[10px] font-medium leading-tight">{error}</p>
              </div>
            )}

            <button onClick={handleFinalize} disabled={isSubmitting} className="w-full py-4 bg-amber-600 text-white rounded-2xl font-bold uppercase tracking-widest text-[10px] flex items-center justify-center gap-3 shadow-lg shadow-amber-900/20 disabled:opacity-50">
              {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Handshaking Terminal...</> : <><CheckCircle2 className="w-4 h-4" /> Finalize Payment</>}
            </button>
            <button onClick={() => setStep(1)} className="w-full mt-4 text-[9px] uppercase font-black text-stone-400 hover:text-stone-600 transition-colors">Modify Tier</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InteractiveBooking;